import { calcularSalario, calcularValor, PRECOS_SUGERIDOS } from './financas';
import { nomeJogador, nomesClubes } from './nomes';
import { FUNCOES, type Funcao } from './posicoes';
import type { Rng } from './rng';
import { SERIE_A_2026, type JogadorReal } from './serie-a-2026';
import { ELENCOS_SERIE_B } from './serie-b-elencos';
import { SERIE_B_2026 } from './serie-b-2026';
import { TATICA_PADRAO } from './tatica';
import { aplicarValoresDeMercado } from './valor-mercado';
import type { Clube, Jogador, Posicao } from './tipos';
import { capacidadeOficial } from './estadios';

/** Composição do elenco: 22 jogadores. */
const COMPOSICAO: [Posicao, number][] = [
  ['G', 3],
  ['Z', 5],
  ['L', 4],
  ['M', 6],
  ['A', 4],
];

/** Características plausíveis por posição (índices de CARACTERISTICAS); as repetidas pesam mais. */
const CARACTERISTICAS_POR_POSICAO: Record<Posicao, number[]> = {
  G: [0, 1, 2, 3],
  Z: [7, 7, 5, 10, 12, 13, 11],
  L: [10, 6, 6, 11, 9, 13, 7],
  M: [4, 11, 11, 7, 10, 9, 12, 13, 8],
  A: [9, 9, 9, 5, 13, 8, 11],
};

function sortearCaracteristicas(pos: Posicao, rng: Rng): [number, number] {
  const lista = CARACTERISTICAS_POR_POSICAO[pos];
  const a = rng.escolher(lista);
  let b = rng.escolher(lista);
  for (let i = 0; i < 10 && b === a; i++) b = rng.escolher(lista);
  if (b === a) b = lista.find((x) => x !== a) ?? a;
  return [a, b];
}

/** Escala de força: um clube de nível 14 tem jogadores em torno de 43, como no original (valores entre 37 e 48). */
export function overallBase(nivelClube: number): number {
  return 15 + nivelClube * 2;
}

export function gerarJogador(id: number, posicao: Posicao, titular: boolean, nivelClube: number, rng: Rng): Jogador {
  let idade = Math.round(rng.normal(26, 4.5));
  idade = Math.min(38, Math.max(17, idade));
  let overall = overallBase(nivelClube) + rng.normal(0, 4.5) + (titular ? 2 : -2);
  if (idade < 21) overall -= 3;
  else if (idade > 33) overall -= 2;
  overall = Math.round(Math.min(92, Math.max(15, overall)));
  return {
    id,
    nome: nomeJogador(rng),
    idade,
    posicao,
    overall,
    energia: 100,
    salario: calcularSalario(overall, idade, posicao, nivelClube),
    valor: calcularValor(overall, idade, nivelClube, posicao),
    lesaoRodadas: 0,
    suspensaoRodadas: 0,
    amarelosAcumulados: 0,
    gols: 0,
    jogos: 0,
    assistencias: 0,
    notaSoma: 0,
    lado: rng.chance(0.5) ? 'E' : 'D',
    caracteristicas: sortearCaracteristicas(posicao, rng),
  };
}

export function gerarClube(
  id: number,
  nome: string,
  sigla: string,
  cores: [string, string],
  nivel: number,
  proximoIdJogador: { valor: number },
  rng: Rng,
  humano = false,
): Clube {
  const reputacao = nivel >= 18 ? 5 : nivel >= 16 ? 4 : nivel >= 13 ? 3 : nivel >= 10 ? 2 : 1;
  const elenco: Jogador[] = [];
  for (const [pos, qtd] of COMPOSICAO) {
    for (let i = 0; i < qtd; i++) {
      const titular = (pos === 'G' && i === 0) || (pos === 'Z' && i < 2) || (pos === 'L' && i < 2) || (pos === 'M' && i < 4) || (pos === 'A' && i < 2);
      elenco.push(gerarJogador(proximoIdJogador.valor++, pos, titular, nivel, rng));
    }
  }
  return {
    id,
    nome,
    sigla,
    nivel,
    reputacao,
    caixa: 3_000_000 + reputacao * 2_500_000,
    capacidade: capacidadeOficial(sigla) ?? Math.round((6000 + reputacao * 11000 + rng.inteiro(4000)) / 100) * 100,
    cor1: cores[0],
    cor2: cores[1],
    humano,
    elenco,
    precos: [...PRECOS_SUGERIDOS],
    balanco: { bilheteria: 0, patrocinio: 0, premios: 0, salarios: 0, transferencias: 0 },
    tatica: TATICA_PADRAO(),
    confiancaDiretoria: 80,
    confiancaTorcida: 75,
    usarPrecosSugeridos: true,
  };
}

const IDADE_DESCONHECIDA = 24;

/**
 * Clubes e jogadores reais da Série A 2026 (ver serie-a-2026.ts). Nome, posição, idade e nacionalidade vêm da fonte;
 * a força (overall) NÃO existe em fonte pública, então segue um critério único: nível do clube (pontos por jogo na temporada)
 * + ruído determinístico + curva de idade. Mesmo critério para todos os clubes.
 */
export function gerarLigaSerieA2026(rng: Rng, proximoIdJogador: { valor: number }, idBase = 0): Clube[] {
  return SERIE_A_2026.map((r, i) => {
    const id = idBase + i;
    const base = gerarClube(id, r.nome, r.sigla, [r.cor1, r.cor2], r.nivel, proximoIdJogador, rng);
    base.tecnico = r.tecnico;
    base.elenco = elencoReal(r.jog, r.nivel, proximoIdJogador, rng);
    // força, valor (€1 mi = R$ 1 mi) e salário saem do valor de mercado real de cada jogador
    aplicarValoresDeMercado(r.sigla, base.elenco, r.nivel, rng);
    return base;
  });
}

/** Monta o elenco de um clube a partir de jogadores reais (nome, posição, idade, nacionalidade, número, lado). */
function elencoReal(jog: JogadorReal[], nivel: number, proximoIdJogador: { valor: number }, rng: Rng): Jogador[] {
  const posicaoDe = (p: string): Posicao => p as Posicao;
  return jog.map(([nome, pos, idadeFonte, nacionalidade, numero, estimada, lado, funcao]) => {
    const idade = idadeFonte > 0 ? idadeFonte : IDADE_DESCONHECIDA;
    const j = gerarJogador(proximoIdJogador.valor++, posicaoDe(pos), numero > 0 && numero <= 11, nivel, rng);
    j.nome = nome;
    j.idade = idade;
    j.nacionalidade = nacionalidade;
    j.numeroCamisa = numero || undefined;
    j.dadosEstimados = [idadeFonte > 0 ? '' : 'idade', estimada ? 'posição' : ''].filter(Boolean).join(',') || undefined;
    if (lado === 'E' || lado === 'D') j.lado = lado;
    if (funcao && (FUNCOES as readonly string[]).includes(funcao)) j.funcao = funcao as Funcao;
    // gerarJogador usa idade sorteada; recalcula força, salário e valor com a idade real
    let overall = overallBase(nivel) + rng.normal(0, 4.5);
    if (idade < 21) overall -= 3;
    else if (idade > 33) overall -= 2;
    j.overall = Math.round(Math.min(92, Math.max(15, overall)));
    j.salario = calcularSalario(j.overall, idade, j.posicao, nivel);
    j.valor = calcularValor(j.overall, idade, nivel, j.posicao);
    return j;
  });
}

/**
 * Brasileirão Série B 2026: 20 clubes reais com os elencos do Transfermarkt (conhecimento/dados/elenco_*.csv). Se algum clube
 * ficar sem arquivo, ele recebe um elenco gerado (nomes fictícios, marcado como estimado).
 */
export function gerarLigaSerieB2026(rng: Rng, proximoIdJogador: { valor: number }, idBase = 0): Clube[] {
  return SERIE_B_2026.map((r, i) => {
    const id = idBase + i;
    const c = gerarClube(id, r.nome, r.sigla, [r.cor1, r.cor2], r.nivel, proximoIdJogador, rng);
    c.tecnico = r.tecnico;
    const real = ELENCOS_SERIE_B[r.sigla];
    // clubes com arquivo em conhecimento/dados/elenco_*.csv usam os jogadores reais; os outros seguem com elenco gerado
    if (real && real.length >= 16) c.elenco = elencoReal(real, r.nivel, proximoIdJogador, rng);
    else for (const j of c.elenco) j.dadosEstimados = 'elenco gerado';
    return c;
  });
}

/** Gera uma liga de `quantidade` clubes com níveis espalhados entre 9 e 19. */
export function gerarLiga(quantidade: number, rng: Rng, proximoIdJogador: { valor: number }): Clube[] {
  const nomes = nomesClubes(rng, quantidade);
  const niveis: number[] = [];
  for (let i = 0; i < quantidade; i++) {
    niveis.push(Math.round(9 + (10 * i) / Math.max(1, quantidade - 1)));
  }
  rng.embaralhar(niveis);
  return nomes.map((n, i) => gerarClube(i, n.nome, n.sigla, n.cores, niveis[i] as number, proximoIdJogador, rng));
}
